/**
 * Shared alert-display helpers — consolidates severity label/color and
 * duration formatting previously duplicated between ActiveAlertsCountBadge.tsx
 * and ProductAlertsSection.tsx. Ported from smart-web's alertDisplay.ts,
 * adapted to the camelCase ProductAlertEpisode shape.
 */
import type { ProductAlertEpisode } from "../../lib/api/products";

export const SEVERITY_LABELS: Record<number, string> = {
  1: "Suggestion",
  2: "Low",
  3: "Medium",
  4: "High",
  5: "Critical",
};

export function severityLabel(severity: number | null | undefined): string {
  if (severity == null) return "—";
  return SEVERITY_LABELS[severity] ?? String(severity);
}

/**
 * pro-web's 1–5 severity colors are already visually distinct (unlike
 * smart-web's MUI palette, which collided Medium with blue) — ported as-is,
 * no collision workaround needed.
 */
export const SEVERITY_COLORS: Record<number, { fg: string; bg: string; border: string }> = {
  1: { fg: "#6B7280", bg: "#F3F4F6", border: "#E5E7EB" },
  2: { fg: "#1D4ED8", bg: "#EFF6FF", border: "#BFDBFE" },
  3: { fg: "#B45309", bg: "#FFFBEB", border: "#FDE68A" },
  4: { fg: "#C2410C", bg: "#FFF7ED", border: "#FED7AA" },
  5: { fg: "#B91C1C", bg: "#FEF2F2", border: "#FECACA" },
};

export function severityColor(severity: number | null | undefined): { fg: string; bg: string; border: string } {
  const sev = Math.min(5, Math.max(1, severity ?? 3));
  return SEVERITY_COLORS[sev] ?? SEVERITY_COLORS[3]!;
}

/** Semantic color key for an alert type — used by the Timeline bars (colored by type, not severity). */
export type AlertTypeColorKey = "error" | "warning" | "info";

export function alertTypeColorKey(type?: string | null): AlertTypeColorKey {
  return type === "fault" ? "error" : type === "alarm" ? "warning" : "info";
}

/** Concrete colors for the Timeline bars — TimelineView takes a raw CSS color, not a semantic key. */
export const ALERT_TYPE_COLORS: Record<AlertTypeColorKey, string> = {
  error: "#DC2626",
  warning: "#D97706",
  info: "#2563EB",
};

export function alertTypeColor(type?: string | null): string {
  return ALERT_TYPE_COLORS[alertTypeColorKey(type)];
}

export function formatAlertDuration(openedMs: number, closedMs: number): string {
  const minutes = Math.max(0, Math.round((closedMs - openedMs) / 60_000));
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  if (hours > 0) {
    return remainingMinutes > 0 ? `${hours}h ${remainingMinutes}m` : `${hours}h`;
  }
  return `${minutes}m`;
}

export function sortAlertsOpenFirst<T extends Pick<ProductAlertEpisode, "active" | "openedAt">>(
  alerts: T[],
): T[] {
  return [...alerts].sort((a, b) => {
    if (a.active !== b.active) return a.active ? -1 : 1;
    return new Date(b.openedAt).getTime() - new Date(a.openedAt).getTime();
  });
}
